/**
 * Billing Page
 * Subscription management, bundle selection, and invoice history
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  awaitAddonStatus,
  useBillingSubscription,
  type SubscriptionWait,
  useBillingBundles,
  useBillingInvoices,
  useCancelMutation,
  useReactivateMutation,
  usePortalSessionMutation,
  useDisableAgentAddonMutation,
  useUndoDisableAgentAddonMutation,
} from '../hooks/useBilling';
import { getInvoicePdfUrl, formatUsd, formatPeriodDate, type Invoice } from '../api/billing';
import { PageHeader } from '../components/layout/index';
import { SettingsLayout } from '../components/settings/SettingsLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { AgentSupportCard } from '../components/billing/AgentSupportCard';
import { AgentAddonDialog } from '../components/billing/AgentAddonDialog';
import { PlanRows, PlanRowsSkeleton } from '../components/billing/PlanRows';
import { PlanIncludedList } from '../components/billing/PlanIncludedList';
import { PromoCodeForm } from '../components/billing/PromoCodeForm';
import { LoadErrorAlert } from '../components/billing/LoadErrorAlert';

/** Subscription status -> chip. `past_due` is a warning ("Payment past due"), never "Expired". */
const SUBSCRIPTION_STATUS_BADGES: Record<
  string,
  { text: string; variant: 'success' | 'primary' | 'warning' | 'secondary' | 'danger' }
> = {
  active: { text: 'Active', variant: 'success' },
  trial: { text: 'Trial', variant: 'primary' },
  past_due: { text: 'Payment past due', variant: 'warning' },
  grace: { text: 'Payment past due', variant: 'warning' },
  cancelled: { text: 'Cancelled', variant: 'secondary' },
  expired: { text: 'Expired', variant: 'danger' },
};

const INVOICE_KIND_LABELS: Record<Invoice['invoiceKind'], string> = {
  subscription: 'Monthly plan',
  listing_fee: 'Listing fee',
  agent_proration: 'Agent support (prorated)',
};

/** "For" column text: the server's description, else the invoice kind. */
function invoiceFor(invoice: Invoice): string {
  return invoice.description ?? INVOICE_KIND_LABELS[invoice.invoiceKind];
}

/** Invoice status chip; an unpaid subscription invoice while the plan is past due reads "Past due". */
function invoiceStatusBadge(invoice: Invoice, planPastDue: boolean) {
  if (invoice.status === 'paid') return { text: 'Paid', variant: 'success' as const };
  if (invoice.invoiceKind === 'subscription' && planPastDue && (invoice.status === 'pending' || invoice.status === 'failed')) {
    return { text: 'Past due', variant: 'warning' as const };
  }
  if (invoice.status === 'failed' || invoice.status === 'uncollectible') {
    return { text: invoice.status === 'failed' ? 'Failed' : 'Uncollectible', variant: 'danger' as const };
  }
  return { text: invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1), variant: 'secondary' as const };
}

type AddonStatus = 'active' | 'cancelling';

export function BillingPage() {
  const navigate = useNavigate();
  // The agent screens' upgrade prompt links here with ?turnOnAgentSupport=1 to open the dialog.
  const [searchParams, setSearchParams] = useSearchParams();
  // An accepted add-on change (turn on / off / keep) is applied asynchronously by the billing
  // subsystem: the next read can still show the old state, so we poll until it reports `expected`.
  const [awaitingAddon, setAwaitingAddon] = useState<SubscriptionWait | null>(null);
  const {
    data: subscription,
    isLoading: isLoadingSubscription,
    outcomeReached: addonConverged,
    timedOut: addonTimedOut,
  } = useBillingSubscription(awaitingAddon);
  const {
    data: bundlesData,
    isLoading: isLoadingBundles,
    isError: isBundlesError,
    refetch: refetchBundles,
    isFetching: isFetchingBundles,
  } = useBillingBundles();
  const {
    data: invoicesData,
    isLoading: isLoadingInvoices,
    hasNextPage: hasMoreInvoices,
    fetchNextPage: fetchMoreInvoices,
    isFetchingNextPage: isFetchingMoreInvoices,
  } = useBillingInvoices();
  const cancelMutation = useCancelMutation();
  const reactivateMutation = useReactivateMutation();
  const disableAddonMutation = useDisableAgentAddonMutation();
  const keepAddonMutation = useUndoDisableAgentAddonMutation();
  const portalMutation = usePortalSessionMutation();

  const [cancelReason, setCancelReason] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [loadingInvoiceId, setLoadingInvoiceId] = useState<string | null>(null);
  const [showAddonDialog, setShowAddonDialog] = useState(() => searchParams.get('turnOnAgentSupport') === '1');
  const [showTurnOffDialog, setShowTurnOffDialog] = useState(false);
  const [addonMessage, setAddonMessage] = useState<string | null>(null);
  const [addonError, setAddonError] = useState<string | null>(null);

  useEffect(() => {
    if (addonConverged) setAwaitingAddon(null);
  }, [addonConverged]);
  const addonUpdating = awaitingAddon !== null && !addonConverged && !addonTimedOut;
  /** The wait ran out before the change showed up: say so instead of "Updating…". */
  const addonSlowNotice = addonTimedOut && !addonConverged;

  /** An add-on change was accepted: show "Updating…" until the read reports `expected`. */
  const awaitAddon = (expected: AddonStatus) => {
    setAwaitingAddon(awaitAddonStatus(expected));
  };

  if (isLoadingSubscription || isLoadingBundles) {
    return (
      <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-64 mb-6" />
        <div className="bg-card rounded-lg border border-border p-5 mb-6">
          <Skeleton className="h-6 w-40 mb-4" />
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-6 w-48" />
        </div>
        <Skeleton className="h-6 w-36 mb-4" />
        <PlanRowsSkeleton />
      </div>
    );
  }

  const bundles = bundlesData?.bundles || [];
  const invoices = invoicesData?.invoices || [];

  const handleCancelSubscription = async () => {
    if (!subscription) return;

    try {
      await cancelMutation.mutateAsync({ reason: cancelReason });
      setShowCancelDialog(false);
      setCancelReason('');
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await reactivateMutation.mutateAsync();
    } catch (error) {
      console.error('Failed to reactivate subscription:', error);
    }
  };

  const handleManageBilling = async () => {
    try {
      const { url } = await portalMutation.mutateAsync();
      window.location.href = url;
    } catch (error) {
      console.error('Failed to create portal session:', error);
    }
  };

  const handleTurnOffAddon = async () => {
    setAddonError(null);
    try {
      await disableAddonMutation.mutateAsync(undefined);
      setShowTurnOffDialog(false);
      setAddonMessage(null);
      awaitAddon('cancelling');
    } catch (err) {
      setShowTurnOffDialog(false);
      setAddonError(err instanceof Error ? err.message : 'Failed to turn off agent support');
    }
  };

  const handleKeepAddon = async () => {
    setAddonError(null);
    try {
      await keepAddonMutation.mutateAsync(undefined);
      setAddonMessage(null);
      awaitAddon('active');
    } catch (err) {
      setAddonError(err instanceof Error ? err.message : 'Failed to keep agent support');
    }
  };

  const handleSelectBundle = (bundleId: string) => {
    navigate(`/settings/billing/checkout?bundleId=${bundleId}`);
  };

  const getStatusBadge = (status: string) => {
    const config = SUBSCRIPTION_STATUS_BADGES[status] ?? SUBSCRIPTION_STATUS_BADGES.expired!;
    return (
      <Badge variant={config.variant} data-testid="subscription-status-badge">
        {config.text}
      </Badge>
    );
  };

  const planPastDue = subscription?.status === 'past_due' || subscription?.status === 'grace';
  const agentAddonActive = subscription?.agentAddon.status === 'active';
  const agentMonthlyCents = (subscription?.agentUnitPriceCents ?? 0) * (subscription?.unitCount ?? 0);

  return (
    <SettingsLayout>
    <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
      <PageHeader
        title="Billing & Subscription"
        subtitle="Manage your subscription and billing settings"
      />

      {/* Subscription Status Card */}
      {subscription && (
        <div className="bg-card rounded-lg border border-border p-5 mb-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground m-0">Current Subscription</h2>
            {getStatusBadge(subscription.status ?? 'expired')}
          </div>

          <div className="mt-4">
            <div className="mb-4">
              <span className="text-xs text-muted-foreground block mb-1">Plan</span>
              <span className="text-lg font-semibold text-foreground">
                {subscription.bundleName || 'No active plan'}
              </span>
            </div>

            <div className="mb-4">
              <span className="text-xs text-muted-foreground block mb-1">Units</span>
              <span className="text-base font-semibold text-foreground">{subscription.unitCount}</span>
            </div>

            {(subscription.unitPriceCents > 0 || agentAddonActive) && (
              <div
                className="mb-4 rounded-lg border border-border p-4 flex flex-col gap-2 text-sm"
                data-testid="plan-breakdown"
              >
                {subscription.unitPriceCents > 0 && (
                  <div className="flex justify-between gap-3" data-testid="plan-breakdown-base">
                    <span className="text-muted-foreground">
                      Base · {subscription.unitCount} unit{subscription.unitCount === 1 ? '' : 's'} ×{' '}
                      {formatUsd(subscription.unitPriceCents)}
                    </span>
                    <span className="font-medium text-foreground">
                      {formatUsd(subscription.unitCount * subscription.unitPriceCents)}
                    </span>
                  </div>
                )}
                {agentAddonActive && (
                  <div className="flex justify-between gap-3" data-testid="plan-breakdown-agent">
                    <span className="text-muted-foreground">
                      Agent support · {subscription.unitCount} unit{subscription.unitCount === 1 ? '' : 's'} ×{' '}
                      {formatUsd(subscription.agentUnitPriceCents ?? 0)}
                    </span>
                    <span className="font-medium text-foreground">{formatUsd(agentMonthlyCents)}</span>
                  </div>
                )}
                {subscription.nextBillingDate && !subscription.cancelAtPeriodEnd && (
                  <div
                    className="flex justify-between gap-3 border-t border-border pt-2"
                    data-testid="plan-breakdown-next-bill"
                  >
                    <span className="font-medium text-foreground">
                      Next bill · {formatPeriodDate(subscription.nextBillingDate)}
                    </span>
                    <span className="font-semibold text-foreground">
                      {formatUsd(subscription.estimatedNextInvoiceCents ?? 0)} + tax
                    </span>
                  </div>
                )}
                <p className="m-0 text-xs text-muted-foreground" data-testid="plan-breakdown-note">
                  Listing fees are charged when you publish, not on this bill.
                </p>
              </div>
            )}

            {/* Trial countdown */}
            {subscription.status === 'trial' && subscription.trialDaysRemaining !== null && (
              <div className="px-4 py-3 bg-primary/5 rounded-lg mb-4">
                <span className="text-sm text-primary">
                  Your trial ends in {subscription.trialDaysRemaining} day{subscription.trialDaysRemaining !== 1 ? 's' : ''}
                </span>
              </div>
            )}

            {/* Cancellation notice */}
            {subscription.cancelAtPeriodEnd && subscription.currentPeriodEnd && (
              <div className="px-4 py-3 bg-muted rounded-lg mb-4">
                <span className="text-sm text-muted-foreground">
                  Your subscription will end on {formatPeriodDate(subscription.currentPeriodEnd, 'UTC', true)}
                </span>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3">
              {(subscription.status === 'trial' || !subscription.hasActiveSubscription) ? (
                <Button
                  onClick={() => document.getElementById('available-plans')?.scrollIntoView({ behavior: 'smooth' })}
                  className="min-h-[44px]"
                >
                  Choose a Plan
                </Button>
              ) : (
                <Button
                  onClick={handleManageBilling}
                  disabled={portalMutation.isPending}
                  className="min-h-[44px]"
                >
                  {portalMutation.isPending
                    ? 'Loading...'
                    : planPastDue
                      ? 'Update payment method'
                      : 'Manage Payment Method'}
                </Button>
              )}

              {subscription.status === 'active' && !subscription.cancelAtPeriodEnd && (
                <Button
                  variant="destructive"
                  onClick={() => setShowCancelDialog(true)}
                  className="min-h-[44px]"
                >
                  Cancel Subscription
                </Button>
              )}

              {subscription.cancelAtPeriodEnd && (
                <Button
                  onClick={handleReactivateSubscription}
                  disabled={reactivateMutation.isPending}
                  className="min-h-[44px]"
                >
                  {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate Subscription'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Agent support add-on */}
      {subscription && (
        <>
          {addonMessage && (
            <div
              role="status"
              data-testid="agent-addon-success"
              className="mb-4 flex items-start justify-between gap-3 p-3 bg-success/10 border border-success/25 rounded-md text-success text-sm"
            >
              <span>{addonMessage}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="min-h-[44px] -my-2"
                data-testid="agent-addon-success-dismiss"
                onClick={() => setAddonMessage(null)}
              >
                Dismiss
              </Button>
            </div>
          )}
          {addonSlowNotice && !addonUpdating && (
            <div
              role="status"
              data-testid="agent-addon-slow-notice"
              className="mb-4 p-3 bg-muted border border-border rounded-md text-foreground text-sm"
            >
              Your change was received but hasn&apos;t shown up here yet. It can take a minute — refresh this page
              shortly.
            </div>
          )}
          {addonError && (
            <div
              role="alert"
              data-testid="agent-addon-error"
              className="mb-4 p-3 bg-destructive/5 border border-destructive/30 rounded-md text-destructive text-sm"
            >
              {addonError}
            </div>
          )}
          <AgentSupportCard
            subscription={subscription}
            onTurnOn={() => setShowAddonDialog(true)}
            onTurnOff={() => setShowTurnOffDialog(true)}
            onKeep={() => void handleKeepAddon()}
            keepPending={keepAddonMutation.isPending}
            updating={addonUpdating}
          />
        </>
      )}

      {/* Available Plans — shown during trial, when expired/no active subscription, or when cancelled pending period end */}
      {(subscription?.status === 'trial' || !subscription?.hasActiveSubscription || (subscription?.status === 'active' && subscription?.cancelAtPeriodEnd)) && (
      <div id="available-plans" className="mb-6">
        <h2 className="text-lg font-semibold text-foreground m-0 mb-4">
          Available Plans
        </h2>

        {isBundlesError && bundles.length === 0 ? (
          <LoadErrorAlert
            message="We couldn't load the available plans. Try again."
            onRetry={() => void refetchBundles()}
            retrying={isFetchingBundles}
            testId="plans-error"
            retryTestId="plans-retry"
          />
        ) : bundles.length === 0 ? (
          <p
            className="m-0 rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground"
            data-testid="plans-empty"
          >
            No plans are available right now. Check back soon.
          </p>
        ) : (
          <PlanRows bundles={bundles} onSelect={handleSelectBundle} />
        )}

        <div className="mt-3 rounded-lg border border-border bg-card p-5">
          <PlanIncludedList />
        </div>

        <div className="mt-3 rounded-lg border border-border bg-card px-5 py-1">
          <PromoCodeForm />
        </div>
      </div>
      )}

      {/* Payment history — every charge is taken automatically, so these are receipts */}
      <div className="bg-card rounded-lg border border-border p-5">
        <h2 className="text-lg font-semibold text-foreground m-0 mb-4">
          Payment History
        </h2>

        {isLoadingInvoices ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-5">
            No payments yet
          </p>
        ) : (
          (() => {
            const hasTaxData = invoices.some(inv => (inv.taxCents ?? 0) > 0);
            return (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-3 px-3 text-left text-xs font-semibold text-muted-foreground">
                        Date
                      </th>
                      <th className="py-3 px-3 text-left text-xs font-semibold text-muted-foreground">
                        For
                      </th>
                      {hasTaxData && (
                        <th className="py-3 px-3 text-left text-xs font-semibold text-muted-foreground">
                          Subtotal
                        </th>
                      )}
                      {hasTaxData && (
                        <th className="py-3 px-3 text-left text-xs font-semibold text-muted-foreground">
                          Tax
                        </th>
                      )}
                      <th className="py-3 px-3 text-left text-xs font-semibold text-muted-foreground">
                        {hasTaxData ? 'Total' : 'Amount'}
                      </th>
                      <th className="py-3 px-3 text-left text-xs font-semibold text-muted-foreground">
                        Status
                      </th>
                      <th className="py-3 px-3 text-left text-xs font-semibold text-muted-foreground">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-border/50">
                        <td className="py-3 px-3 text-sm text-foreground">
                          {formatPeriodDate(invoice.createdAt, 'UTC', true)}
                        </td>
                        <td className="py-3 px-3 text-sm text-foreground" data-testid={`invoice-for-${invoice.id}`}>
                          {invoiceFor(invoice)}
                        </td>
                        {hasTaxData && (
                          <td className="py-3 px-3 text-sm text-foreground" data-testid={`invoice-subtotal-${invoice.id}`}>
                            {formatUsd(invoice.subtotalExcludingTaxCents ?? invoice.amountCents, { alwaysDecimals: true })}
                          </td>
                        )}
                        {hasTaxData && (
                          <td className="py-3 px-3 text-sm text-foreground" data-testid={`invoice-tax-${invoice.id}`}>
                            {formatUsd(invoice.taxCents ?? 0, { alwaysDecimals: true })}
                          </td>
                        )}
                        <td className="py-3 px-3 text-sm font-semibold text-foreground" data-testid={`invoice-total-${invoice.id}`}>
                          {formatUsd(invoice.amountCents, { alwaysDecimals: true })}
                        </td>
                        <td className="py-3 px-3">
                          {(() => {
                            const badge = invoiceStatusBadge(invoice, planPastDue);
                            return (
                              <Badge variant={badge.variant} data-testid={`invoice-status-${invoice.id}`}>
                                {badge.text}
                              </Badge>
                            );
                          })()}
                        </td>
                        <td className="py-3 px-3">
                          {invoice.status === 'paid' && invoice.stripeInvoiceId && (
                            <Button
                              data-testid={`invoice-pdf-button-${invoice.id}`}
                              variant="ghost"
                              size="sm"
                              className="min-h-[44px] text-muted-foreground"
                              disabled={loadingInvoiceId === invoice.id}
                              onClick={async () => {
                                setLoadingInvoiceId(invoice.id);
                                try {
                                  const result = await getInvoicePdfUrl(invoice.id);
                                  // Charges are automatic, so the receipt is the right document; the invoice PDF is the fallback.
                                  window.open(result.receiptUrl ?? result.pdfUrl, '_blank');
                                } catch (err) {
                                  console.error('Failed to fetch invoice PDF URL:', err);
                                } finally {
                                  setLoadingInvoiceId(null);
                                }
                              }}
                            >
                              {loadingInvoiceId === invoice.id ? '...' : 'View receipt'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {hasMoreInvoices && (
                  <div className="flex justify-center pt-4">
                    <Button
                      data-testid="invoice-load-more"
                      variant="outline"
                      className="min-h-[44px]"
                      disabled={isFetchingMoreInvoices}
                      onClick={() => void fetchMoreInvoices()}
                    >
                      {isFetchingMoreInvoices ? 'Loading…' : 'Load older invoices'}
                    </Button>
                  </div>
                )}
              </div>
            );
          })()
        )}
      </div>

      {/* Agent support: turn on (consent + prorated charge) / turn off (ends at period end) */}
      <AgentAddonDialog
        open={showAddonDialog}
        onOpenChange={(next) => {
          setShowAddonDialog(next);
          if (!next && searchParams.has('turnOnAgentSupport')) {
            const params = new URLSearchParams(searchParams);
            params.delete('turnOnAgentSupport');
            setSearchParams(params, { replace: true });
          }
        }}
        onEnabled={(message) => {
          setShowAddonDialog(false);
          setAddonError(null);
          setAddonMessage(message);
          awaitAddon('active');
        }}
      />
      <ConfirmDialog
        isOpen={showTurnOffDialog}
        onClose={() => setShowTurnOffDialog(false)}
        onConfirm={() => void handleTurnOffAddon()}
        title="Turn off agent support?"
        message={`Agents keep working until ${formatPeriodDate(subscription?.nextBillingDate)}; you won't be charged for them after that.`}
        confirmText="Turn off"
        isDestructive
        isLoading={disableAddonMutation.isPending}
      />

      {/* Cancel Subscription Dialog */}
      {showCancelDialog && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowCancelDialog(false)}
        >
          <div
            className="bg-card rounded-xl p-6 max-w-md w-[90%] border border-border"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-foreground m-0 mb-3">
              Cancel Subscription
            </h2>

            <p className="text-sm text-muted-foreground mb-4">
              Your subscription will remain active until the end of your current billing period. Are you sure you want to cancel?
            </p>

            <div className="mb-5">
              <label className="block text-xs font-semibold text-foreground mb-2">
                Reason for cancellation (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Help us improve by telling us why you're cancelling"
                className="w-full p-3 text-sm border border-input rounded-md min-h-[80px] resize-vertical bg-background text-foreground"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowCancelDialog(false)}
                className="min-h-[44px]"
              >
                Keep Subscription
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelSubscription}
                disabled={cancelMutation.isPending}
                className="min-h-[44px]"
              >
                {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Subscription'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </SettingsLayout>
  );
}

export default BillingPage;
